import React, { useRef } from 'react'
import imageUrlBuilder from '@sanity/image-url'

// @ts-expect-error
import sanityClient from '@sanity/client'
import useEmblaCarousel from 'embla-carousel-react'
import './care-team-carousel.css'

import { CareTeamProps } from './CareTeam'
import LeftArrowIcon from 'src/styles/icons/LeftArrowIcon'
import RightArrowIcon from 'src/styles/icons/RightArrowIcon'
import { getVisibleSpecialties } from './utils'
import { getWidgetUrl } from '../joinWidget'

const client = sanityClient({
  projectId: process.env.GATSBY_SANITY_PROJECT_ID!,
  dataset: process.env.GATSBY_SANITY_DATASET!,
  useCdn: false,
})

export const CareTeamCarousel: React.FC<{ data: CareTeamProps }> = ({
  data,
}) => {
  const builder = imageUrlBuilder(client)
  const { title, subtitle, providers, backgroundColor } = data

  const getImageUrl = (asset: any) => (asset ? builder.image(asset).url() : '')
  const widgetURL = getWidgetUrl()

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'start' })

  const containerRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const setContainerRef = (key: string, ref: HTMLDivElement | null) => {
    containerRefs.current[key] = ref
  }

  const scrollPrev = () => {
    emblaApi?.scrollPrev()
  }

  const scrollNext = () => {
    emblaApi?.scrollNext()
  }

  return (
    <div className={`care-team-carousel__background  ${backgroundColor}`}>
      <div className={`care-team-carousel__container ${backgroundColor}`}>
        {title && <span className="care-team-carousel__title">{title}</span>}
        {subtitle && (
          <span className="care-team-carousel__subtitle">{subtitle}</span>
        )}
        <div className="care-team-carousel__carousel-wrapper" ref={emblaRef}>
          <div className="care-team-carousel__carousel-container">
            {(providers ?? []).map((provider: any) => {
              const key = provider?._key
              const main = provider?.content?.main
              const slug = main?.slug?.current
              const providerURL = `/team/${slug}`
              const imageUrl = getImageUrl(main?.image?.asset)

              const { visible, remainingCount } = getVisibleSpecialties(
                main?.specialties || [],
                { current: containerRefs.current[key] },
                'carousel'
              )

              return (
                <div
                  className="care-team-carousel__carousel-slide"
                  key={provider?._key}
                >
                  <div className="care-team-carousel__card_item">
                    <div className="care-team-carousel__provider_details">
                      <div className="care-team-carousel__provider_image_container">
                        <img
                          className="care-team-carousel__provider_image"
                          src={imageUrl || ''}
                          alt="Provider Image"
                          style={{
                            borderRadius: '1rem',
                          }}
                        />
                      </div>
                      <div className="care-team-carousel__provider_meta">
                        <div className="care-team-carousel__provider_title">
                          {main?.title}
                        </div>
                        <div
                          className="care-team-carousel__specialties_container"
                          ref={ref => setContainerRef(key, ref)}
                        >
                          {visible.map((specialty: string, index: number) => (
                            <div
                              key={index}
                              className="care-team-carousel__specialty"
                            >
                              {specialty}
                            </div>
                          ))}
                          {remainingCount > 0 && (
                            <div className="care-team-carousel__specialty">
                              +{remainingCount}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="care-team-carousel__actions">
                      <button
                        type="button"
                        className="care-team-carousel__appointment_button"
                        onClick={() => {
                          window.open(
                            `${widgetURL}`,
                            '_blank',
                            'noopener,noreferrer'
                          )
                        }}
                      >
                        Make an appointment
                      </button>
                      <a
                        className="care-team-carousel__learn_more"
                        target="__blank"
                        href={providerURL}
                      >
                        Learn More
                      </a>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
        <div className="care-team-carousel__carousel-controls">
          <button
            type="button"
            aria-label="Scroll Left"
            onClick={scrollPrev}
            className="care-team-carousel__carousel-prev"
          >
            <LeftArrowIcon />
          </button>

          <button
            type="button"
            aria-label="Scroll Right"
            onClick={scrollNext}
            className="care-team-carousel__carousel-next"
          >
            <RightArrowIcon />
          </button>
        </div>
      </div>
    </div>
  )
}

export type { CareTeamProps }
